import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper: Validate email format
const isValidEmail = (email) => email && email.includes('@');

// Helper: Subscribe to newsletter API
const subscribeToNewsletter = async (email) => {
  const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return response.json();
};

// Sub-component: Submit button with states
const SubmitButton = ({ isSubmitting, isSubmitted }) => (
  <Button
    type="submit"
    size="lg"
    className="bg-gray-900 hover:bg-black text-white font-bold px-10 h-14 text-lg rounded-full shadow-2xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
    disabled={isSubmitting || isSubmitted}
  >
    {isSubmitted && (
      <>
        <CheckCircle2 className="mr-2 h-5 w-5" />
        Subscribed!
      </>
    )}
    {isSubmitting && !isSubmitted && 'Subscribing...'}
    {!isSubmitting && !isSubmitted && 'Subscribe'}
  </Button>
);

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const data = await subscribeToNewsletter(email);
      
      if (data.success) {
        setIsSubmitted(true);
        toast.success(data.message);
        setEmail('');
        setTimeout(() => setIsSubmitted(false), 3000);
      } else {
        toast.error(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="newsletter" className="relative bg-gradient-to-br from-orange-500 to-orange-600 py-20 overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-10 left-20 w-64 h-64 bg-white rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-10 right-20 w-80 h-80 bg-black rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-6 shadow-2xl">
          <Mail className="h-10 w-10 text-orange-500" />
        </div>

        <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
          Stay Updated!
        </h2>
        
        <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
          Join our email list to get notified about new puzzle books, exclusive offers, 
          and brain-teasing challenges delivered straight to your inbox.
        </p>

        <form onSubmit={handleSubmit} className="max-w-xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-14 px-6 text-lg border-0 bg-white shadow-xl rounded-full focus:ring-4 focus:ring-white/50"
                disabled={isSubmitting || isSubmitted}
                required
              />
            </div>
            <SubmitButton isSubmitting={isSubmitting} isSubmitted={isSubmitted} />
          </div>
        </form>

        <p className="mt-6 text-sm text-white/80">
          We respect your privacy. Unsubscribe at any time.
        </p>
      </div>
    </div>
  );
};

export default Newsletter;
